/**
 * 消息中心弹层。
 * 原型里这 4 条消息完全内联在 app.js 的弹层函数里，没有数据源、没有未读数、
 * 没有已读状态，首页铃铛红点是 CSS 常亮装饰。此处全部落库。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MemberMessage } from '@hew3/shared';
import { api } from '../../lib/api.ts';
import { Badge, Sheet, StateBox } from '../../components/ui.tsx';
import { useUi } from '../../store/ui.ts';

interface MessagesResponse {
  items: MemberMessage[];
  unread: number;
}

const kindTone: Record<MemberMessage['kind'], 'h' | 'e' | 'w' | 'danger' | 'ghost'> = {
  alert: 'danger',
  report: 'e',
  service: 'h',
  system: 'ghost',
};

const kindLabel: Record<MemberMessage['kind'], string> = {
  alert: '预警',
  report: '报告',
  service: '服务',
  system: '系统',
};

export function MessagesSheet() {
  const open = useUi((s) => s.sheet === 'messages');
  const closeSheet = useUi((s) => s.closeSheet);
  const showToast = useUi((s) => s.showToast);
  const queryClient = useQueryClient();

  const messagesQuery = useQuery({
    queryKey: ['messages'],
    queryFn: () => api.get<MessagesResponse>('/api/member/messages'),
    enabled: open,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['messages'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/member/messages/${id}/read`),
    onSuccess: invalidate,
  });

  const markAll = useMutation({
    mutationFn: () => api.post<{ updated: number }>('/api/member/messages/read-all'),
    onSuccess: (data) => {
      showToast(data.updated > 0 ? `已将 ${data.updated} 条消息标记为已读` : '没有未读消息');
      invalidate();
    },
  });

  return (
    <Sheet open={open} title="消息中心" onClose={closeSheet}>
      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="text-tiny text-muted">
          {messagesQuery.data ? `${messagesQuery.data.unread} 条未读` : '加载中…'}
        </span>
        <button
          type="button"
          className="btn sm"
          disabled={markAll.isPending || (messagesQuery.data?.unread ?? 0) === 0}
          onClick={() => markAll.mutate()}
        >
          全部标记已读
        </button>
      </div>

      {messagesQuery.isLoading && <StateBox kind="loading" title="正在加载消息…" />}
      {messagesQuery.isError && <StateBox kind="error" title="消息加载失败" desc="请稍后重试" />}

      {messagesQuery.data?.items.length === 0 && (
        <StateBox title="暂无消息" desc="预警、报告归档与服务进展会在这里通知您" />
      )}

      {messagesQuery.data?.items.map((m) => (
        <button
          key={m.id}
          type="button"
          className="list-item"
          style={{ width: '100%', textAlign: 'left', border: 'none', opacity: m.read ? 0.62 : 1 }}
          onClick={() => {
            if (!m.read) markRead.mutate(m.id);
          }}
        >
          {!m.read && <span className="dot-unread" style={{ marginTop: 6 }} />}
          <div className="li-main">
            <div className="li-t">{m.title}</div>
            <div className="li-s">{m.body}</div>
            <div className="li-s" style={{ marginTop: 6 }}>
              {m.source} · {m.relativeTime}
            </div>
          </div>
          <div className="li-r">
            <Badge tone={kindTone[m.kind]}>{kindLabel[m.kind]}</Badge>
          </div>
        </button>
      ))}
    </Sheet>
  );
}
